// src/features/simulators/mobile-kiosk/components/MobileKioskConfigForm.tsx

import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Grid,
} from '@mui/material';
import {
  Smartphone,
  Computer,
  Key,
  CreditCard,
  Phone,
  QrCode,
  Person as PersonIcon,
  DevicesOther as TerminalIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { ReservationNumberControl } from '../../shared';
import { TerminalType } from '../types';
import { GuestType, KeyDeliveryMethod } from '../../shared/types/journey.types';

export interface MobileKioskConfigValues {
  terminalType: TerminalType;
  guestType: GuestType;
  keyDeliveryMethod: KeyDeliveryMethod;
  reservationNumber: string;
  autoGenerateReservation: boolean;
}

interface MobileKioskConfigFormProps {
  values: MobileKioskConfigValues;
  onChange: (values: MobileKioskConfigValues) => void;
}

export const MobileKioskConfigForm: React.FC<MobileKioskConfigFormProps> = ({
  values,
  onChange,
}) => {
  const translate = useTranslate();

  const update = (partial: Partial<MobileKioskConfigValues>) => {
    onChange({ ...values, ...partial });
  };

  const isMobile = values.terminalType === TerminalType.MOBILE;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <MyFormSection
          title={translate('resources.simulators.mobile_kiosk.terminal_type.label')}
          icon={<TerminalIcon />}
          variant="outlined"
        >
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{translate('resources.simulators.mobile_kiosk.terminal_type.label')}</InputLabel>
            <Select
              value={values.terminalType}
              onChange={(e) => update({ terminalType: e.target.value as TerminalType })}
              label={translate('resources.simulators.mobile_kiosk.terminal_type.label')}
            >
              <MenuItem value={TerminalType.MOBILE}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Smartphone fontSize="small" />
                  {translate('resources.simulators.mobile_kiosk.terminal_type.mobile')}
                </Box>
              </MenuItem>
              <MenuItem value={TerminalType.PHYSICAL}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Computer fontSize="small" />
                  {translate('resources.simulators.mobile_kiosk.terminal_type.physical')}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
            {isMobile
              ? translate('resources.simulators.mobile_kiosk.info.mobile')
              : translate('resources.simulators.mobile_kiosk.info.physical')
            }
          </Alert>
        </MyFormSection>
      </Grid>

      <Grid item xs={12} md={6}>
        <MyFormSection
          title={translate('resources.simulators.kiosk.guest_type.label')}
          icon={<PersonIcon />}
          variant="outlined"
        >
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{translate('resources.simulators.kiosk.guest_type.label')}</InputLabel>
            <Select
              value={values.guestType}
              onChange={(e) => update({ guestType: e.target.value as GuestType })}
              label={translate('resources.simulators.kiosk.guest_type.label')}
            >
              <MenuItem value={GuestType.RESERVATION}>
                {translate('resources.simulators.kiosk.guest_type.reservation')}
              </MenuItem>
              <MenuItem value={GuestType.WALKING}>
                {translate('resources.simulators.kiosk.guest_type.walking')}
              </MenuItem>
            </Select>
          </FormControl>

          <ReservationNumberControl
            value={values.reservationNumber}
            autoGenerate={values.autoGenerateReservation}
            onValueChange={(value) => update({ reservationNumber: value })}
            onAutoGenerateChange={(autoGenerate) => update({ autoGenerateReservation: autoGenerate })}
            prefix="RES"
          />
        </MyFormSection>
      </Grid>

      <Grid item xs={12}>
        <MyFormSection
          title={translate('resources.simulators.kiosk.key_delivery.label')}
          icon={<Key />}
          variant="outlined"
        >
          <FormControl fullWidth>
            <InputLabel>{translate('resources.simulators.kiosk.key_delivery.label')}</InputLabel>
            <Select
              value={values.keyDeliveryMethod}
              onChange={(e) => update({ keyDeliveryMethod: e.target.value as KeyDeliveryMethod })}
              label={translate('resources.simulators.kiosk.key_delivery.label')}
            >
              <MenuItem value={KeyDeliveryMethod.MAGNETIC_CARD}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCard fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.magnetic_card')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.RFID_CARD}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCard fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.rfid_card')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.CONVENTIONAL_KEY}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Key fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.conventional_key')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.DIGITAL_PIN}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.digital_pin')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.DIGITAL_MOBILE}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.digital_mobile')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.QR_DISPENSER}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QrCode fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.qr_dispenser')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.MANUAL}>
                {translate('resources.simulators.kiosk.key_delivery.manual')}
              </MenuItem>
            </Select>
          </FormControl>
        </MyFormSection>
      </Grid>
    </Grid>
  );
};
